import React from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../spinner/Spinner";
import Swal from "sweetalert2";
import { useAdminPurchaseById, useApproveAdminPurchase, useCancelAdminPurchase } from "../../hooks/useInventory";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";

const statusColor = (s) => {
  if (s === "approved") return { bg: "#dcfce7", color: "#166534" };
  if (s === "pending") return { bg: "#fef9c3", color: "#854d0e" };
  if (s === "cancelled") return { bg: "#fee2e2", color: "#991b1b" };
  return { bg: "#f3f4f6", color: "#374151" };
};

const AdminPurchaseShow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const purchaseId = searchParams.get("id");

  const { data, isLoading, refetch } = useAdminPurchaseById(purchaseId);
  const approveMutation = useApproveAdminPurchase();
  const cancelMutation = useCancelAdminPurchase();

  const purchase = data?.data;

  const handleApprove = () => {
    Swal.fire({
      title: "Approve Purchase?",
      text: "Approving this purchase will update the warehouse inventory.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, approve it!",
    }).then((result) => {
      if (result.isConfirmed) approveMutation.mutate(purchase.id, { onSuccess: refetch });
    });
  };

  const handleCancel = () => {
    Swal.fire({
      title: "Cancel Purchase?",
      text: "Provide a reason for cancellation (optional):",
      icon: "warning",
      input: "text",
      inputPlaceholder: "Reason for cancellation...",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
    }).then((result) => {
      if (result.isConfirmed) cancelMutation.mutate({ purchaseId: purchase.id, reason: result.value || "" }, { onSuccess: refetch });
    });
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/admin-purchases")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold">Purchase Order Details</h1>
        </div>

        {isLoading ? (
          <Spinner loading />
        ) : !purchase ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-red-500">Purchase not found.</div>
        ) : (
          <>
            {/* Header card */}
            <div className="bg-white rounded-lg shadow p-6 mb-4">
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Purchase #{purchase.id}</p>
                  <h2 className="text-xl font-bold">{purchase.vendor?.name}</h2>
                  {purchase.reference_number && <p className="text-gray-500 text-sm">Ref: {purchase.reference_number}</p>}
                </div>
                <div className="flex gap-2 items-center">
                  {(() => { const sc = statusColor(purchase.status); return (
                    <span style={{ padding: "4px 14px", borderRadius: "9999px", fontSize: "0.85rem", fontWeight: 600, backgroundColor: sc.bg, color: sc.color }}>
                      {purchase.status}
                    </span>
                  ); })()}
                  {purchase.status === "pending" && (
                    <>
                      <button onClick={handleApprove} disabled={approveMutation.isLoading} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50">
                        {approveMutation.isLoading ? "Approving..." : "Approve"}
                      </button>
                      <button onClick={handleCancel} disabled={cancelMutation.isLoading} className="px-4 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50 text-sm disabled:opacity-50">
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
                {[
                  ["Purchase Date", purchase.purchase_date || "—"],
                  ["Total Cost", null, purchase.total_cost],
                  ["Amount Paid", null, purchase.amount_paid],
                  ["Discount", null, purchase.discount_amount],
                ].map(([label, text, amount]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">
                      {text || <CurrencyFormatter amount={amount || 0} />}
                    </p>
                  </div>
                ))}
              </div>

              {purchase.notes && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Notes</p>
                  <p className="text-sm text-gray-700 mt-0.5">{purchase.notes}</p>
                </div>
              )}
            </div>

            {/* Items table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-800">Purchase Items</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Product", "Quantity", "Cost Price", "Subtotal", "Notes"].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(purchase.items || []).map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-5 py-4 text-sm font-medium text-gray-900">{item.product_name}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{item.quantity}</td>
                        <td className="px-5 py-4 text-sm text-gray-600"><CurrencyFormatter amount={item.cost_price} /></td>
                        <td className="px-5 py-4 text-sm font-semibold"><CurrencyFormatter amount={(item.quantity || 0) * (item.cost_price || 0)} /></td>
                        <td className="px-5 py-4 text-sm text-gray-500">{item.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayouts>
  );
};

export default AdminPurchaseShow;
